import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useUser } from '@src/hooks/useUser';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import { useExecutionReports } from '@src/providers/ExecutionReportsContext';
import {
  bpsToPercent,
  Button,
  ButtonVariants,
  FormControlSizes,
  SideEnum,
  useGlobalToasts,
  useSecuritiesContext,
  useSocketClient,
  type Quote,
} from '@talos/kyoko';
import { memo } from 'react';

import type { QuoteMarketWithBestBidOffer } from '../../../../utils/rfq';
import { isQuoteTradeSideDisabled } from '../utils';
import { ACCEPT_BUTTON_STATE, useQuoteAcceptButtonState } from './useQuoteAcceptButtonState';

interface QuoteAcceptButtonProps {
  /** The full Quote entity */
  quote: Quote;
  /** Which side this button trades */
  side: SideEnum;
  /** The pre-computed best market for this side (bid for Sell, offer for Buy) */
  bestMarket: QuoteMarketWithBestBidOffer | undefined;
  careOrderSide?: SideEnum;
}

/**
 * A blotter action button that implements the double-click-to-trade pattern.
 *
 * First click: locks in the current best price.
 * Second click: sends the acceptQuote request.
 * Button shows a loading state until the quote reaches a terminal status.
 */
export const QuoteAcceptButton = memo(function QuoteAcceptButton({
  quote,
  side,
  bestMarket,
  careOrderSide,
}: QuoteAcceptButtonProps) {
  const user = useUser();
  const webSocketClient = useSocketClient();
  const { add: addToast } = useGlobalToasts();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { executionReportSub } = useExecutionReports();
  const { allowedSlippage } = useTradingSettings();
  const { truncateCareOrderID } = useFeatureFlag();

  const security = securitiesBySymbol.get(quote.Symbol);
  const disabled = isQuoteTradeSideDisabled({
    quote,
    quoteMarket: bestMarket,
    side,
    user,
    careOrderSide,
  });

  const { state, onClick, onReset } = useQuoteAcceptButtonState({
    quote,
    side,
    bestMarket,
    allowedSlippage: allowedSlippage?.value ? bpsToPercent(allowedSlippage.value) : undefined,
    security,
    disabled,
    truncateCareOrderID,
    webSocketClient,
    executionReportsSub: executionReportSub,
    addToast,
  });

  const isArmed = state === ACCEPT_BUTTON_STATE.Armed;
  const isLoading = state === ACCEPT_BUTTON_STATE.Loading;
  const label = side === SideEnum.Sell ? 'Sell' : 'Buy';

  const variant = side === SideEnum.Sell ? ButtonVariants.Negative : ButtonVariants.Positive;

  return (
    <Button
      size={FormControlSizes.Small}
      variant={variant}
      disabled={disabled || isLoading}
      loading={isLoading}
      onClick={onClick}
      onMouseLeave={onReset}
      width="65px"
      data-status={state}
      data-testid={`quote-accept-button-${side.toLowerCase()}`}
    >
      {isArmed ? 'Confirm' : label}
    </Button>
  );
});
